/**
 * Shared shape + "client declined to share" placeholder values for the
 * Client Information step — used by both FlowPlayerPage's real entry form
 * and FlowPreviewPanel's read-only simulation, so the two can't drift on
 * what counts as "filled in" or what the placeholder button actually fills
 * in. See backend apps/flows/models.py::CallLogClientInfo for how this is
 * stored (its own table, kept separate from the rest of the call log).
 */

export interface ClientInfoValue {
  first_name: string;
  last_name: string;
  date_of_birth: string; // YYYY-MM-DD, matches <input type="date">
  email: string;
  phone_number: string;
  declined_to_share: boolean;
}

export const EMPTY_CLIENT_INFO: ClientInfoValue = {
  first_name: "",
  last_name: "",
  date_of_birth: "",
  email: "",
  phone_number: "",
  declined_to_share: false,
};

// Obviously-fake stand-ins for a caller who won't share their real
// information — Staff still need *something* on file rather than getting
// stuck on this step, so this fills the required fields (name, DOB,
// phone). Email is left blank; it's the one field that was never
// required. `declined_to_share` is what actually flags the row as a
// placeholder for anyone reading it back later — not the values
// themselves, which a real "Jane Doe" could coincidentally match.
export const CLIENT_INFO_PLACEHOLDER: ClientInfoValue = {
  first_name: "Jane",
  last_name: "Doe",
  date_of_birth: "1900-01-01",
  email: "",
  phone_number: "0000000000",
  declined_to_share: true,
};

/** Required before Continue enables: name, last name, DOB, phone. Email
 * stays optional — a caller not wanting to give it out shouldn't block the
 * rest of the call. */
export function isClientInfoComplete(value: ClientInfoValue): boolean {
  return (
    value.first_name.trim() !== "" &&
    value.last_name.trim() !== "" &&
    value.date_of_birth.trim() !== "" &&
    value.phone_number.trim() !== ""
  );
}
